import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCampaigns } from 'modules/messagingInsights/selectors';
import { selectPreviewThumbnails } from 'modules/templateIndex';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { useGetIsExpanded, useSort } from '../hooks';
import { usePagination } from '../hooks/usePagination';
import { SnippetsContext } from './SnippetsContext';
import { useQueryBaseLevelSnippets } from '../hooks/queries/useQueryBaseLevelSnippets';
import { getBaseLevelTableRows } from '../utils/getBaseLevelTableRows';
import { useQueryBaseLevelBreakdown } from '../hooks/queries/useQueryBaseLevelBreakdown';
import { getCampaignIdToMetadataMap, getCsvRows, getTableWithBreakdownRows } from '../utils';
import {
  BaseLevelBreakdownApiRequest,
  BaseLevelSnippetsAPIRequest,
  Campaign,
  SnippetsTableModes,
  SortObj,
  StoredCampaignBreakdowns,
  StoredSnippetBreakdowns,
  ThumbnailResp,
} from '../types';

export type ContextProps = {
  mode: SnippetsTableModes;
  campaignIds: number[];
  templateIdOpt?: number;
  startTime?: string;
  endTime?: string;
  sort?: SortObj;
};

export const SnippetsContextProvider: FC<React.PropsWithChildren<ContextProps>> = ({
  mode,
  campaignIds,
  templateIdOpt,
  children,
  startTime = '',
  endTime = '',
  sort,
}) => {
  const [debouncedSearchTerm, searchTerm, setSearchTerm] = useDebouncedState('', 500);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const { sortBy, sortDirection, handleSort } = useSort(sort);
  const { pagination, handlePageChange, setCount, resetPagination } = usePagination();
  const [storedSnippetBreakdowns, setStoredSnippetBreakdowns] = useState(
    {} as StoredSnippetBreakdowns,
  );
  const [storedCampaignBreakdowns, setStoredCampaignBreakdowns] = useState(
    {} as StoredCampaignBreakdowns,
  );
  const campaigns = useSelector(selectCampaigns) || ([] as Campaign[]);
  const thumbnails = useSelector(selectPreviewThumbnails) || ({} as ThumbnailResp);
  const campaignIdToMetadataMap = getCampaignIdToMetadataMap(campaigns) || {};
  const sortObj = { sortBy, sortDirection };

  const sharedParams = {
    campaignIds,
    startTime,
    endTime,
    templateIdOpt,
  };
  const baseLevelSnippetsRequest: BaseLevelSnippetsAPIRequest = {
    ...sharedParams,
    searchOpt: debouncedSearchTerm,
    sort: sortObj,
    pagination: {
      limit: pagination.limit,
      offset: pagination.offset,
    },
  };

  const { baseLevelSnippetData, info, baseLevelSnippetsLoading, hasBaseLevelDataError } =
    useQueryBaseLevelSnippets(baseLevelSnippetsRequest);
  const tableCount = info.count;

  const { isExpanded, expandedBaseLevelSnippets, updateIsExpanded } = useGetIsExpanded(
    baseLevelSnippetData,
    mode,
  );

  const pendingBreakdownIds = expandedBaseLevelSnippets.filter(
    (id) => !Object.keys(storedSnippetBreakdowns).includes(id.toString()),
  );

  const baseLevelBreakdownRequest: BaseLevelBreakdownApiRequest = {
    ...sharedParams,
    baseLevelSnippetIds: pendingBreakdownIds,
    showCampaignBreakdown: mode === SnippetsTableModes.MessagingInsights,
  };
  const { hasBreakdownError } = useQueryBaseLevelBreakdown(
    baseLevelBreakdownRequest,
    setStoredSnippetBreakdowns,
    setStoredCampaignBreakdowns,
  );

  const baseLevelRows = getBaseLevelTableRows(baseLevelSnippetData, showCampaigns);
  const tableData = getTableWithBreakdownRows(
    baseLevelRows,
    storedSnippetBreakdowns,
    storedCampaignBreakdowns,
    expandedBaseLevelSnippets,
    sortObj,
    showCampaigns,
    campaignIdToMetadataMap,
    thumbnails,
  );

  const csvData = getCsvRows(
    baseLevelSnippetData,
    storedSnippetBreakdowns,
    storedCampaignBreakdowns,
    campaignIdToMetadataMap,
    expandedBaseLevelSnippets,
    showCampaigns,
  );

  useEffect(() => {
    setCount(tableCount);
  }, [tableCount]);

  const handleSearch = (searchStr: string) => {
    setSearchTerm(searchStr);
    resetPagination();
  };

  return (
    <SnippetsContext.Provider
      value={{
        mode,
        tableData,
        csvData,
        searchTerm,
        sortBy,
        sortDirection,
        pagination,
        hasError: hasBaseLevelDataError || hasBreakdownError,
        isLoading: baseLevelSnippetsLoading,
        isExpanded,
        showCampaigns,
        setShowCampaigns,
        handleSearch,
        handleSort,
        handlePageChange,
        updateIsExpanded,
        startTime,
      }}
    >
      {children}
    </SnippetsContext.Provider>
  );
};
